import type { Emitter } from 'nanoevents'
import type { Events } from '@/proxy/shared/events'
import { topicMatches } from '@shared/util/topic-matcher'
import { createProxyEvents } from '@/proxy/shared/events'

/**
 * Dev events — no parent iframe to subscribe to.
 * Returns both the `on` function (for ProxyApiInstance) and
 * the raw `emitter` so host stubs can fire events.
 */
export function createDevEvents() {
  const emitter: Emitter<Events> = createProxyEvents()

  // No subscribeToEvents() — no parent to listen to.
  // Host stubs fire events directly on the emitter.

  const on = <T extends string>(
    topicPattern: T,
    callback: T extends '@history'
      ? Events['@history']
      : T extends '@visibility'
        ? Events['@visibility']
        : Events['@message'],
  ) => {
    if (topicPattern === '@history')
      return emitter.on('@history', callback as Events['@history'])
    if (topicPattern === '@visibility')
      return emitter.on('@visibility', callback as Events['@visibility'])
    if (topicPattern === '@message')
      return emitter.on('@message', callback as Events['@message'])

    return emitter.on('@message', (message) => {
      if (topicMatches(message.topic, topicPattern))
        (callback as Events['@message'])(message)
    })
  }

  return { on, emitter }
}
